import { useEffect, useState } from "react";
import type { EqmsController } from "../../../controllers/useEqms";
import * as eqmsApi from "../../../controllers/eqmsApi";
import { friendlyMessage } from "../../../controllers/eqmsApi";
import type { DocReviewTask } from "../../../models/eqms";
import { useUserDirectory } from "../../../controllers/useUserDirectory";
import { UserDirError, UserInline } from "../../components/UserCell";

const ROLE_LABEL: Record<DocReviewTask["role"], string> = { REVIEWER: "검토자", APPROVER: "승인자" };

// The eQMS module's review inbox: every pending review/approval task assigned to me, with inline
// approve/reject actions. Acting on a task reloads the list.
export function EqmsInboxView({ eqms }: { eqms: EqmsController }) {
  const [tasks, setTasks] = useState<DocReviewTask[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [actingId, setActingId] = useState<string | null>(null);
  const [comments, setComments] = useState<Record<string, string>>({});

  const dir = useUserDirectory(eqms.getToken);
  useEffect(() => {
    dir.ensure(tasks.map((t) => t.requesterId));
  }, [tasks, dir.ensure]);

  const load = () => {
    setLoading(true);
    setError(null);
    eqmsApi.myReviewInbox(eqms.getToken)
      .then((list) => setTasks(list.filter((t) => t.action === "PENDING")))
      .catch((e) => setError(friendlyMessage(e)))
      .finally(() => setLoading(false));
  };

  useEffect(load, [eqms.getToken]);

  async function act(task: DocReviewTask, action: "approve" | "reject") {
    setActingId(task.id);
    try {
      await eqmsApi.actOnReview(eqms.getToken, task.id, action, comments[task.id] ?? "");
      load();
    } catch (e) {
      setError(friendlyMessage(e));
    } finally {
      setActingId(null);
    }
  }

  return (
    <div>
      <div style={{ marginBottom: 16 }}>
        <h2 style={{ margin: 0 }}>검토함</h2>
        <div className="note" style={{ marginTop: 4 }}>내게 할당된 문서 검토/승인 대기 목록</div>
      </div>

      {error && (
        <div className="note" style={{ background: "#fdeef0", marginBottom: 16 }}>
          <b style={{ color: "var(--amber)" }}>오류:</b> {error}
        </div>
      )}

      {loading ? (
        <div className="center">불러오는 중…</div>
      ) : tasks.length === 0 ? (
        <div className="sdui-list-empty">대기 중인 검토가 없습니다.</div>
      ) : (
        <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
          <UserDirError dir={dir} />
          {tasks.map((task) => (
            <div className="info-card" key={task.id}>
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", flexWrap: "wrap", gap: 8 }}>
                <div>
                  <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
                    <button className="btn" style={{ width: "auto" }} onClick={() => eqms.openDetail("documents", task.documentId)}>
                      문서 {task.documentId.slice(0, 8)}
                    </button>
                    <span className="tag">{ROLE_LABEL[task.role]}</span>
                  </div>
                  <div className="note" style={{ marginTop: 4 }}>
                    요청자: {task.requesterId ? <UserInline id={task.requesterId} dir={dir} /> : "알 수 없음"}
                    {task.dueDate && <> · 마감: {task.dueDate}</>}
                  </div>
                </div>
              </div>

              <div style={{ display: "flex", gap: 8, alignItems: "flex-end", marginTop: 10, flexWrap: "wrap" }}>
                <label className="sdui-field" style={{ flex: 1, minWidth: 200 }}>
                  <span className="sdui-label">의견 (선택)</span>
                  <input
                    className="sdui-input"
                    value={comments[task.id] ?? ""}
                    onChange={(e) => setComments((m) => ({ ...m, [task.id]: e.target.value }))}
                  />
                </label>
                <button
                  className="btn primary"
                  style={{ width: "auto" }}
                  disabled={actingId === task.id}
                  onClick={() => act(task, "approve")}
                >
                  {actingId === task.id ? "처리 중…" : "승인"}
                </button>
                <button
                  className="btn"
                  style={{ width: "auto" }}
                  disabled={actingId === task.id}
                  onClick={() => act(task, "reject")}
                >
                  반려
                </button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
